import {
  SkyButton,
  SkyDataGrid,
  SkyDropdown,
  SkyInput,
  SkyMessageBar,
  SkyText,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { useLocation, useSearch } from "wouter";
import type {
  CreateTenantInput,
  TenantStatus,
  TenantView,
  UpdateTenantBody,
} from "@shared/types/tenants";
import { TopbarActionsPortal } from "@/components/navigation/TopbarActionsPortal";
import { DeleteTenantDialog } from "@/components/platform/DeleteTenantDialog";
import { DisableTenantDialog } from "@/components/platform/DisableTenantDialog";
import { TenantOffboardingDrawer } from "@/components/platform/TenantOffboardingDrawer";
import { DefaultTargetPicker } from "@/components/platform/DefaultTargetPicker";
import { TenantResourcesDrawer } from "@/components/platform/TenantResourcesDrawer";
import { TenantConnectorsDrawer } from "@/components/platform/TenantConnectorsDrawer";
import { TenantDialog, type TenantFormValues } from "@/components/platform/TenantDialog";
import {
  EmptyState,
  ErrorState,
  GridSkeleton,
  ScreenNote,
} from "@/components/platform/ScreenState";
import { useGroupAccessEnabled } from "@/hooks/use-group-access";
import { usePermissions } from "@/hooks/use-permissions";
import { useTenantResourceTargets } from "@/hooks/use-tenant-provisioning";
import {
  useCreateTenant,
  useDeletedTenants,
  useDeleteTenant,
  useTenants,
  useUpdateTenant,
} from "@/hooks/use-tenants";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { reportFailureShown } from "@/lib/feedback/failure";
import { AddIcon, TenantsIcon } from "@/lib/icons";
import { LIST_GRID_PAGINATION, pixelColumnLayout } from "@/lib/grid";
import { usePinnedColumnEdge } from "@/lib/gridPinned";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { buildTenantColumns } from "./tenantsColumns";

/**
 * Tenants (FR-20, canvas 10): the deployment's tenants, one row each.
 *
 * Round 6 T-04 (owner 2026-10-02: "Rename. No need to use Customer. Keep it simple. Tenant,
 * Instance name"): the customer above each tenant, one to one since platform 0022, folded into
 * it (platform 0027), so this page, its nav entry, its button and its dialog say Tenants, New
 * tenant and Tenant; `/admin/customers` redirects here. One entity with two names, each with one
 * job (round 6 A1, owner 2026-10-01 23:25): the **Display name** (spaces allowed, editable;
 * `tenants.display_name`) and the **Name** (`tenants.name`: fixed once created, used in vault,
 * database and resource names), and one Status. `?tenant=<id>` opens that tenant's dialog.
 *
 * No row is ever removed: a tenant id is referenced by its instances and every audit row ever
 * written about them, so `status` moves to `disabled`. F32 PR3 (owner decision 2026-10-10): a
 * DISABLED tenant can then be deleted ("Delete", typed Name and a reason). A deleted tenant stays
 * disabled, leaves every list but the "Deleted" filter, and its row opens its offboarding list
 * (read-only, to copy for Infra); skyData removes no resource.
 *
 * The route guard in `App.tsx` refuses this screen to anyone without `platform.tenants.manage`;
 * a create also needs `platform.instances.manage`, which the same role (PlatformAdmin) holds.
 * The test ids keep their round 5 words (`customers-page`, `customers-grid`, `new-customer` and
 * the rest): e2e specs read them, and no person sees them.
 */

const STATUS_FILTER_OPTIONS = [
  { value: "all", text: "All statuses" },
  { value: "active", text: "Active" },
  { value: "disabled", text: "Disabled" },
  { value: "deleted", text: "Deleted" },
];

/** The status filter: a tenant status, every one but deleted, or only the deleted tenants. */
type StatusFilter = "all" | TenantStatus | "deleted";

/**
 * Each column's minimum (its header or its widest usual value, plus the 32 px padding, and 22 px
 * for a sort icon), so the percentages share the pane in proportion and the table has a floor
 * (`pixelColumnLayout`). Actions holds Edit and a menu (Connectors, Disable or Enable, Delete)
 * and is pinned at the right edge (#1071). The floor is 920 px (the sum, 919, rounded up to 8), inside the pane at 1024 px with the nav's
 * 48 px rail (`Sidebar` collapses it at 1024 px and below), so nothing sits under Actions at 1024
 * px or from about 1229 px up. With the 260 px drawer open between 1025 and 1229 px the pane is
 * 716 to 920 px, so the grid scrolls and the last columns pass under Actions (the edge line
 * shows). Display name and Name are wide enough for a 23 character name and a 15 character Name
 * (`tenants.pinnedActions.test.tsx`). 1,112 px with the directory column, which is off while
 * group access is. Round 6 A1 dropped the Key Vault column; it is read-only in Edit.
 */
const MIN_COLUMN_WIDTH: Record<string, number> = {
  displayName: 192,
  website: 105,
  name: 133,
  entraTenantId: 200,
  instanceCount: 120,
  resources: 128,
  status: 116,
  actions: 126,
};

/** The create and update bodies with the fields ADR 0010 adds (design §7.2). */
type CreateBody = CreateTenantInput & { resourcesTargetId?: string };
type UpdateBody = UpdateTenantBody & { defenderForStorage?: boolean | null };

/** Only the fields that changed, so the audit row's `fields` says what the person changed. */
function tenantChanges(before: TenantView, values: TenantFormValues): Partial<UpdateBody> {
  return {
    ...(values.displayName === before.displayName ? {} : { displayName: values.displayName }),
    ...(values.website === before.website ? {} : { website: values.website }),
    ...(values.entraTenantId === before.entraTenantId
      ? {}
      : { entraTenantId: values.entraTenantId }),
    ...(values.status === before.status ? {} : { status: values.status }),
    ...(values.defenderForStorage === undefined ||
    values.defenderForStorage === (before.resources?.defenderForStorage ?? null)
      ? {}
      : { defenderForStorage: values.defenderForStorage }),
  };
}

/**
 * A tenant's link (`/admin/tenants/<id>`, `?tenant=<id>`, and `/admin/customers?tenant=<id>`
 * redirects here with it) opens that tenant once the list has arrived; set during render,
 * guarded by the id handled. Nested under `/admin/tenants` (App.tsx mounts every route with
 * wouter's `nest`), so the location here is "/" or "/<id>". A link to a tenant no row holds
 * (removed, or a stale bookmark) is returned as missing, so the page says so instead of doing
 * nothing.
 */
function useLinkedTenant(
  search: string,
  tenants: TenantView[] | undefined,
  open: (tenant: TenantView) => void,
): [string | null, (missing: string | null) => void] {
  const [location] = useLocation();
  const linkedTenant =
    location.split("/").filter(Boolean)[0] ?? new URLSearchParams(search).get("tenant");
  const [handledLink, setHandledLink] = useState<string | null>(null);
  const [missingLink, setMissingLink] = useState<string | null>(null);
  if (linkedTenant && handledLink !== linkedTenant && tenants) {
    setHandledLink(linkedTenant);
    const target = tenants.find((row) => row.id === linkedTenant);
    if (target) open(target);
    else setMissingLink(linkedTenant);
  }
  return [missingLink, setMissingLink];
}

/**
 * The notice after New tenant (#921): the request is accepted and the set-up runs in the
 * background, so it does not say "created" while the row still reads Setting up.
 */
const TENANT_REQUESTED_MESSAGE =
  "Its setup runs in the background, usually about 3 minutes. Its row turns Active when it is ready.";

/** No tenant to list: none matches the filters, or none exists yet (New tenant). */
function NoTenants({
  isFiltered,
  onCreate,
  disabled,
}: {
  isFiltered: boolean;
  onCreate: () => void;
  disabled: boolean;
}) {
  return (
    <EmptyState
      testId="customers-empty"
      icon={TenantsIcon}
      title={isFiltered ? "No tenants match those filters" : "No tenants yet"}
      message={
        isFiltered
          ? "Clear the search or the status filter to see the rest."
          : "A tenant holds its people, its instances and its data, kept apart from every other tenant. Create the first one. Its first instances are created with it."
      }
      action={
        isFiltered ? null : (
          <SkyButton appearance="primary" icon={AddIcon} onClick={onCreate} disabled={disabled}>
            New tenant
          </SkyButton>
        )
      }
    />
  );
}

/** The tenants, and how many, with the rule that a tenant is disabled before it is deleted. */
function TenantsGrid({
  rows,
  layout,
}: {
  rows: TenantView[];
  layout: { columns: SkyDataGridColumn<TenantView>[]; floor: number };
}) {
  // Actions stay at the right edge (as on Connections): Disable is never scrolled out of reach.
  const pinnedEdge = usePinnedColumnEdge<HTMLDivElement>(true);
  return (
    <>
      <div
        ref={pinnedEdge}
        style={{ "--tenants-grid-floor": `${layout.floor}px` } as CSSProperties}
      >
        <SkyDataGrid<TenantView>
          data={rows}
          columns={layout.columns}
          {...LIST_GRID_PAGINATION}
          getRowId={(row) => row.id}
          className="grid-pinned-last [&_table]:min-w-(--tenants-grid-floor) [&_th]:whitespace-normal"
          testId="customers-grid"
        />
      </div>
      <SkyText size={200} color="muted" block>
        {rows.length} {rows.length === 1 ? "tenant" : "tenants"}. A tenant is disabled before it can
        be deleted. A deleted tenant&apos;s stored data is removed later by Skypoint. Its Name
        cannot change after creation.
      </SkyText>
    </>
  );
}

/** The rows the search term and the status filter leave. */
function filterTenantRows(
  data: TenantView[],
  query: string,
  statusFilter: StatusFilter,
): TenantView[] {
  const term = query.trim().toLowerCase();
  return data.filter((tenant) => {
    if (statusFilter !== "all" && statusFilter !== "deleted" && tenant.status !== statusFilter) {
      return false;
    }
    if (term === "") return true;
    return (
      tenant.displayName.toLowerCase().includes(term) ||
      (tenant.entraTenantId ?? "").toLowerCase().includes(term) ||
      (tenant.website ?? "").toLowerCase().includes(term) ||
      tenant.name.toLowerCase().includes(term)
    );
  });
}

/**
 * The tenants a link may open, or undefined while it must wait. A deleted tenant's offboarding
 * list has no dialog, so its link never waits for the mode.
 */
function linkableTenants({
  tenants,
  deleted,
  linkedIsDeleted,
  modeUnknown,
  hasLink,
}: {
  tenants: TenantView[] | undefined;
  deleted: { data?: TenantView[]; isError: boolean };
  linkedIsDeleted: boolean;
  modeUnknown: boolean;
  hasLink: boolean;
}): TenantView[] | undefined {
  if (linkedIsDeleted) return [...(tenants ?? []), ...(deleted.data ?? [])];
  if (tenants && !modeUnknown && (!hasLink || deleted.data || deleted.isError)) {
    return [...tenants, ...(deleted.data ?? [])];
  }
  return undefined;
}

/** The list's loading, failed, empty or filled state. */
function TenantsBody({
  listed,
  rows,
  layout,
  isFiltered,
  onCreate,
  createDisabled,
}: {
  listed: {
    isPending: boolean;
    isError: boolean;
    error: unknown;
    refetch: () => Promise<unknown>;
  };
  rows: TenantView[];
  layout: { columns: SkyDataGridColumn<TenantView>[]; floor: number };
  isFiltered: boolean;
  onCreate: () => void;
  createDisabled: boolean;
}): ReactNode {
  if (listed.isPending) {
    return <GridSkeleton rows={4} label="Loading tenants…" testId="customers-loading" />;
  }
  if (listed.isError) {
    return (
      <ErrorState
        title="The tenant list did not load"
        error={listed.error}
        onRetry={() => void listed.refetch()}
        testId="customers-error"
      />
    );
  }
  if (rows.length === 0) {
    return <NoTenants isFiltered={isFiltered} onCreate={onCreate} disabled={createDisabled} />;
  }
  return <TenantsGrid rows={rows} layout={layout} />;
}

export default function AdminTenantsPage() {
  const zone = useDisplayTz();
  const notice = useNotice();
  const search = useSearch();
  // Each dialog on this screen prints its own failure inside it and stays open, so a failure is
  // only logged here (ux-s10 §3).
  const tenants = useTenants({ followSetup: true });
  const create = useCreateTenant();
  const update = useUpdateTenant();
  const remove = useDeleteTenant();
  const { can } = usePermissions();

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  // F32 PR3: the disabled tenant being deleted, and the deleted tenant whose list is open.
  const [deleting, setDeleting] = useState<TenantView | null>(null);
  const [offboardingFor, setOffboardingFor] = useState<Pick<
    TenantView,
    "id" | "displayName"
  > | null>(null);
  const [editing, setEditing] = useState<TenantView | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [disabling, setDisabling] = useState<TenantView | null>(null);
  // T-51: the tenant whose connector choice is open, set or locked by Skypoint.
  const [connectorsFor, setConnectorsFor] = useState<TenantView | null>(null);
  // Group access on (`GET /me` `features.groupAccess`): the directory id is its one reader, so
  // its column and its field show only then (round 5).
  const showEntraDirectory = useGroupAccessEnabled();
  // ADR 0010: the mode is the server's own answer on the targets view (`mode`), not a guess from
  // the tenant rows: an empty per-tenant deployment has no rows. Until it is known, a PlatformAdmin
  // cannot open the tenant dialog, so the shared-mode Key Vault field never shows by default.
  const [resourcesFor, setResourcesFor] = useState<string | null>(null);
  const canManage = can("platform.tenants.manage");
  const targets = useTenantResourceTargets(canManage);
  const modeUnknown = canManage && !targets.data;
  const showResources = targets.data?.mode === "per-tenant";
  const placement = showResources ? (targets.data ?? null) : null;
  const resourcesTenant = tenants.data?.find((row) => row.id === resourcesFor) ?? null;

  // A tenant's link (`/admin/tenants/<id>`, `?tenant=<id>`, and `/admin/customers?tenant=<id>`
  // redirects here with it) opens that tenant once the list has arrived; set during render,
  // guarded by the id handled. Nested under `/admin/tenants` (App.tsx mounts every route with
  // wouter's `nest`), so the location here is "/" or "/<id>".
  // The deleted tenants: for the Deleted filter, and for a link to one (the L1 team's offboarding
  // message links to the tenant), which opens its offboarding list. A link waits for the mode
  // too, so the dialog never opens with the wrong fields.
  const [location] = useLocation();
  const hasLink =
    location.split("/").filter(Boolean).length > 0 || new URLSearchParams(search).has("tenant");
  const deleted = useDeletedTenants(statusFilter === "deleted" || hasLink);
  const linkedId =
    location.split("/").filter(Boolean)[0] ?? new URLSearchParams(search).get("tenant");
  // A deleted tenant's offboarding list has no dialog, so its link never waits for the mode.
  const linkedIsDeleted = Boolean(linkedId && deleted.data?.some((row) => row.id === linkedId));
  const linkable = linkableTenants({
    tenants: tenants.data,
    deleted,
    linkedIsDeleted,
    modeUnknown,
    hasLink,
  });
  const [missingLink, setMissingLink] = useLinkedTenant(search, linkable, (target) => {
    if (target.deletedAt) {
      setOffboardingFor(target);
      return;
    }
    setEditing(target);
    update.reset();
    setFormOpen(true);
  });

  const listed = statusFilter === "deleted" ? deleted : tenants;
  const rows = useMemo(
    () => filterTenantRows(listed.data ?? [], query, statusFilter),
    [listed.data, query, statusFilter],
  );

  // #921: `?new=1` (`NEW_TENANT_ADDRESS`, Overview's "Create your first tenant" and the Instances
  // screen's "Create a tenant") opens New tenant once the mode is known, set during render like a
  // tenant's link, guarded so closing the dialog does not reopen it.
  const wantsNew = canManage && new URLSearchParams(search).get("new") === "1";
  const [handledNew, setHandledNew] = useState(false);
  if (wantsNew && !handledNew && !modeUnknown) {
    setHandledNew(true);
    setEditing(null);
    setFormOpen(true);
  }

  function openCreate() {
    if (modeUnknown) return;
    setEditing(null);
    create.reset();
    setFormOpen(true);
  }

  function openEdit(tenant: TenantView) {
    if (modeUnknown) return;
    setEditing(tenant);
    update.reset();
    setFormOpen(true);
  }

  function createTenant(values: TenantFormValues) {
    // One request, all or nothing (round 5): the tenant and the ticked instances.
    const body: CreateBody = {
      displayName: values.displayName,
      website: values.website ?? "",
      // Asked for only while group access is on; otherwise not sent at all.
      ...(showEntraDirectory ? { entraTenantId: values.entraTenantId } : {}),
      name: values.name,
      ...(values.keyVaultName === "" ? {} : { keyVaultName: values.keyVaultName }),
      instances: values.instances,
      destinationType: values.destinationType,
      ...(values.resourcesTargetId === undefined
        ? {}
        : { resourcesTargetId: values.resourcesTargetId }),
    };
    create.mutate(body, {
      onSuccess: () => {
        setFormOpen(false);
        // #921: the request is accepted, not finished. The tenant's set-up (and its instances')
        // runs in the background, so the notice says so rather than "created".
        notice.success(TENANT_REQUESTED_MESSAGE, {
          title:
            values.instances.length === 1
              ? "Tenant and instance requested"
              : "Tenant and instances requested",
        });
      },
      onError: (error) => reportFailureShown(error),
    });
  }

  /**
   * An edit is ONE request (round 6 A1): the tenant's PUT, and one `tenant.update` audit row. A
   * failure keeps the dialog open with the server's sentence.
   */
  function saveEdit(before: TenantView, values: TenantFormValues) {
    const body = tenantChanges(before, values);
    if (Object.keys(body).length === 0) {
      setFormOpen(false);
      return;
    }
    update.mutate(
      { id: before.id, body: body as UpdateTenantBody },
      {
        onSuccess: () => {
          setFormOpen(false);
          notice.success("Tenant updated");
        },
        onError: (error) => reportFailureShown(error),
      },
    );
  }

  function saveTenant(values: TenantFormValues) {
    if (editing) {
      saveEdit(editing, values);
      return;
    }
    createTenant(values);
  }

  function confirmDisable() {
    if (!disabling) return;
    const next: TenantStatus = disabling.status === "active" ? "disabled" : "active";
    update.mutate(
      { id: disabling.id, body: { status: next } },
      {
        onSuccess: () => {
          notice.success(next === "disabled" ? "Tenant disabled" : "Tenant enabled");
          setDisabling(null);
        },
        onError: (error) => reportFailureShown(error),
      },
    );
  }

  function confirmDelete(body: { confirmName: string; reason: string }) {
    if (!deleting) return;
    const target = deleting;
    remove.mutate(
      { id: target.id, body },
      {
        onSuccess: () => {
          notice.success("Tenant deleted");
          setDeleting(null);
          // The list Infra needs, at once, to copy.
          setOffboardingFor({ id: target.id, displayName: target.displayName });
        },
        onError: (error) => reportFailureShown(error),
      },
    );
  }

  const columns = buildTenantColumns({
    zone,
    showEntraDirectory,
    showResources,
    modeUnknown,
    onOffboarding: setOffboardingFor,
    onEdit: openEdit,
    onConnectors: setConnectorsFor,
    onToggle: setDisabling,
    onDelete: (row) => {
      remove.reset();
      setDeleting(row);
    },
    onResources: setResourcesFor,
  });
  const layout = pixelColumnLayout(columns, { minimums: MIN_COLUMN_WIDTH, selection: false });

  const isFiltered = query.trim() !== "" || statusFilter !== "all";

  return (
    <div className="flex flex-col gap-4" data-testid="customers-page">
      <TopbarActionsPortal>
        <SkyButton
          appearance="primary"
          icon={AddIcon}
          onClick={openCreate}
          disabled={modeUnknown}
          testId="new-customer"
        >
          New tenant
        </SkyButton>
      </TopbarActionsPortal>

      <ScreenNote
        title="A tenant is the billing and identity boundary"
        message={`Each tenant's data and credentials are kept separate from every other tenant's. Its instances, such as Development and Production, are separate workspaces inside it.${
          showEntraDirectory
            ? " A tenant's Microsoft Entra directory is where the directory groups that grant access come from."
            : ""
        } Deleting a tenant stops all its work at once. Its stored data is removed later by Skypoint, following the offboarding steps.`}
      />

      {missingLink ? (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          title="That tenant was not found"
          message="The link names a tenant that is not here. It may be out of date. Search the tenants below instead."
          onDismiss={() => setMissingLink(null)}
          testId="customers-tenant-link-missing"
        />
      ) : null}

      {showResources && targets.data ? <DefaultTargetPicker view={targets.data} /> : null}

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-72">
          <SkyInput
            label="Search tenants"
            type="search"
            value={query}
            clearable
            onChange={(event) => setQuery(event.target.value)}
            onClear={() => setQuery("")}
            testId="customer-search"
          />
        </div>
        <div className="w-52">
          <SkyDropdown
            label="Status"
            options={STATUS_FILTER_OPTIONS}
            selectedOptions={[statusFilter]}
            onOptionSelect={(values) => setStatusFilter((values[0] as StatusFilter) ?? "all")}
            testId="customer-status-filter"
          />
        </div>
      </div>

      {targets.isError ? (
        <ErrorState
          title="Could not read placement targets"
          error={targets.error}
          onRetry={() => void targets.refetch()}
          testId="targets-error"
        />
      ) : null}

      <TenantsBody
        listed={listed}
        rows={rows}
        layout={layout}
        isFiltered={isFiltered}
        onCreate={openCreate}
        createDisabled={modeUnknown}
      />

      {/* The key remounts the dialog on every opening and on every row, which is how its fields
          are seeded from the row without an effect that synchronises a form to a prop. */}
      <TenantDialog
        key={`tenant-${formOpen}-${editing?.id ?? "new"}`}
        open={formOpen}
        onOpenChange={setFormOpen}
        tenant={editing}
        onSubmit={saveTenant}
        isSaving={editing ? update.isPending : create.isPending}
        showEntraDirectory={showEntraDirectory}
        placement={placement}
        error={editing ? update.error : create.error}
      />

      <TenantResourcesDrawer tenant={resourcesTenant} onClose={() => setResourcesFor(null)} />
      <TenantConnectorsDrawer tenant={connectorsFor} onClose={() => setConnectorsFor(null)} />

      <DeleteTenantDialog
        key={`delete-${deleting?.id ?? "none"}`}
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        tenant={deleting}
        onConfirm={confirmDelete}
        isSaving={remove.isPending}
        error={remove.error}
      />
      <TenantOffboardingDrawer tenant={offboardingFor} onClose={() => setOffboardingFor(null)} />

      <DisableTenantDialog
        key={`disable-${disabling?.id ?? "none"}`}
        open={disabling !== null}
        onOpenChange={(open) => {
          if (!open) setDisabling(null);
        }}
        tenant={disabling}
        onConfirm={confirmDisable}
        isSaving={update.isPending}
        error={update.error}
      />
    </div>
  );
}
